import axios from "axios";
import { OptimizerFinlandGroupCertificationTypeEnum } from "shared/enums/opti_enums";
import { getRawUrlBase, OptimizerResourcesEnum } from "shared/enums/resourceMappings";
import { Gain, GainPayload } from "./types";
import { Identifier } from "react-admin";

interface GenerateBaseGainParams {
  group_certification_id: number;
  certified_power: number;
  market: OptimizerFinlandGroupCertificationTypeEnum;
  isBase: boolean;
  site_id: Identifier;
}

export const getCertifiedPowerFields = (
  certified_power: number,
  market: OptimizerFinlandGroupCertificationTypeEnum,
) => {
  switch (market) {
    case OptimizerFinlandGroupCertificationTypeEnum.FCR_N:
      return {
        certifiedPowerFields: { fcr_n_commitment_fi: certified_power },
        activeGainFields: { fcr_n_fi: certified_power },
      };
    case OptimizerFinlandGroupCertificationTypeEnum.FCR_D_SYMMETRICAL:
      return {
        certifiedPowerFields: { fcr_d_up_commitment_fi: certified_power, fcr_d_down_commitment_fi: certified_power },
        activeGainFields: { fcr_d_up_fi: certified_power, fcr_d_down_fi: certified_power },
      };
    case OptimizerFinlandGroupCertificationTypeEnum.AFRR_ASYMMERICAL_FINLAND:
      return {
        certifiedPowerFields: { afrr_commitment: certified_power },
        activeGainFields: { afrr_fr: certified_power },
      };
    default:
      return { certifiedPowerFields: {}, activeGainFields: {} };
  }
};

/**
 * Generates a gain payload for a group certification.
 *
 * @param params - Object containing group_certification_id, certified_power, market, and isBase flag.
 * @returns A GainPayload object with appropriate commitments based on market type.
 */
export const generateBaseGain = ({
  group_certification_id,
  certified_power = 0,
  market,
  isBase,
  site_id,
}: GenerateBaseGainParams): GainPayload => {
  const baseFields = {
    target_soc_min_pct: 0.5,
    target_soc_max_pct: 0.5,
    afrr_commitment: 0,
    fcr_commitment_fr: 0,
    fcr_n_commitment_fi: 0,
    fcr_d_up_commitment_fi: 0,
    fcr_d_down_commitment_fi: 0,
    group_certification_id,
    availability_combination: isBase ? 0 : 1,
  };

  const baseGainFields: Gain = {
    [site_id]: {
      fcr_fr: 0,
      afrr_fr: 0,
      fcr_n_fi: 0,
      fcr_d_up_fi: 0,
      fcr_d_down_fi: 0,
    },
  };

  if (isBase) {
    return {
      ...baseFields,
      gains: baseGainFields,
    };
  }

  const { certifiedPowerFields, activeGainFields } = getCertifiedPowerFields(certified_power, market);

  return {
    ...baseFields,
    ...certifiedPowerFields,
    gains: { [site_id]: { ...baseGainFields[site_id], ...activeGainFields } },
  };
};

/**
 * Extracts error message from Axios error response.
 *
 * @param error - The error from a rejected promise.
 * @returns The error message string.
 */
const extractErrorMessage = (error: any): string => {
  // Check if it's an Axios error with response data
  if (error?.response?.data) {
    const { message, error_code } = error.response.data;
    if (message) {
      return error_code ? `${error_code}: ${message}` : message;
    }
  }

  // Fallback to error message
  return error?.message || "Unknown error";
};

/**
 * Creates gain records for a group certification.
 *
 * @param group_certification_id - The ID of the group certification.
 * @param certified_power - The certified power value.
 * @param market - The market type.
 * @returns A promise that resolves when all gains are created.
 * @throws An error with descriptive message(s) if any gains fail to create.
 */
export const createGains = async (
  group_certification_id: number,
  certified_power: number,
  market: OptimizerFinlandGroupCertificationTypeEnum,
  site_id: Identifier,
): Promise<void> => {
  const kwargs = {
    group_certification_id,
    certified_power,
    market,
  };

  const gains = [
    generateBaseGain({ ...kwargs, isBase: true, site_id }),
    generateBaseGain({ ...kwargs, isBase: false, site_id }),
  ];

  const results = await Promise.allSettled(
    gains.map((gain) => axios.post(getRawUrlBase(OptimizerResourcesEnum.OptiGains), gain)),
  );

  const failures = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
  if (failures.length > 0) {
    // Extract error messages from all failures
    const errorMessages = failures.map((failure) => extractErrorMessage(failure.reason)).join("; ");

    console.error("Some gains failed to create:", failures);
    throw new Error(errorMessages);
  }
};
